/**
 * 建会话的幂等键：**同一次意图复用同一把**。
 *
 * 同一次点击里重试 / 双击 / 断线恢复带同一把；成功或明确 4xx 才放掉。
 * 网络错误与 5xx 不放——不知道服务端收没收到时换钥匙就是在赌。
 *
 * 钥匙必须绑在意图上，不能全屏只记一把。否则一次网络/5xx 之后用户
 * 换了项目或模式再点，旧钥匙配上新摘要，首击就是 409。
 *
 * 连续两次「新会话」也不能从 (mode, projectId) **派生**钥匙本身：
 * 那会把两次新意图撞成一条。fingerprint 只用来查找「这一次还没结束
 * 的那把 UUID」，成功放掉之后同 fingerprint 会再 mint 一把新的。
 */

import { isDefinitiveRejection } from './recovery-key'

export interface CreateConversationKeyStore {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

export const CONVERSATION_CREATE_KEYS_STORAGE_KEY = 'npc.conversation-create'
const PERSIST_VERSION = 2 as const

/**
 * 一次建会话意图。业务字段对齐后端 `createConversationDigest`
 * （项目、标题、mode、team_policy、agent、排序后的 roster）。
 *
 * `creatorUserId` 同时进入后端摘要与浏览器 ledger：sessionStorage 在同一
 * 标签页退出再登录后还在，不隔开真人会让后来登录的人接住前一位创建者的
 * 不确定请求。它来自 `/auth/me`，调用方不许用入口名、常量或空串代替。
 */
export interface ConversationCreateIntent {
  readonly creatorUserId: string
  readonly projectId: string | null
  readonly mode: string
  readonly title: string
  readonly teamPolicy?: string
  readonly agentProfileId?: string
  readonly rosterProfileIds?: readonly string[]
}

type PersistedLedger = {
  readonly v: typeof PERSIST_VERSION
  readonly keys: Record<string, string>
}

function normalizeMode(mode: string): string {
  return mode === '' ? 'solo' : mode
}

function normalizeTeamPolicy(mode: string, teamPolicy: string | undefined): string {
  const normalizedMode = normalizeMode(mode)
  if (normalizedMode === 'team' && (teamPolicy === undefined || teamPolicy === '')) {
    return 'lulu_auto'
  }
  return teamPolicy ?? ''
}

/** 对齐 google/uuid 的 String 结果；接口通常已经是这个形状，这里也守住 URL 深链。 */
function normalizeUUID(value: string): string {
  let hex = value.toLowerCase()
  if (hex.startsWith('urn:uuid:')) hex = hex.slice('urn:uuid:'.length)
  if (hex.startsWith('{') && hex.endsWith('}')) hex = hex.slice(1, -1)
  hex = hex.replaceAll('-', '')
  if (!/^[0-9a-f]{32}$/.test(hex)) return value
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

function normalizeOptionalUUID(value: string | undefined): string {
  if (!value) return ''
  const normalized = normalizeUUID(value)
  return normalized === '00000000-0000-0000-0000-000000000000' ? '' : normalized
}

/** 稳定 fingerprint：同意图同串，意图变了就变。不用拼接，避免 ("ab","c") 撞 ("a","bc")。 */
export function conversationCreateFingerprint(intent: ConversationCreateIntent): string {
  if (intent.creatorUserId.trim() === '') {
    throw new Error('creatorUserId is required for conversation create idempotency')
  }
  const roster = [...(intent.rosterProfileIds ?? [])].map(normalizeUUID).sort()
  return JSON.stringify([
    normalizeUUID(intent.creatorUserId),
    'conversation-create',
    intent.projectId === null ? '' : normalizeUUID(intent.projectId),
    intent.title,
    normalizeMode(intent.mode),
    normalizeTeamPolicy(intent.mode, intent.teamPolicy),
    normalizeOptionalUUID(intent.agentProfileId),
    roster,
  ])
}

function isPersistedLedger(value: unknown): value is PersistedLedger {
  if (value === null || typeof value !== 'object') return false
  const rec = value as { v?: unknown; keys?: unknown }
  if (rec.v !== PERSIST_VERSION) return false
  if (rec.keys === null || typeof rec.keys !== 'object' || Array.isArray(rec.keys)) return false
  return Object.values(rec.keys as Record<string, unknown>).every(
    (item) => typeof item === 'string',
  )
}

export class CreateConversationKeys {
  private readonly issued = new Map<string, string>()

  constructor(
    private readonly persist: CreateConversationKeyStore | null = null,
    private readonly persistKey: string = CONVERSATION_CREATE_KEYS_STORAGE_KEY,
  ) {}

  keyFor(intent: ConversationCreateIntent, mint: () => string = () => crypto.randomUUID()): string {
    const fp = conversationCreateFingerprint(intent)
    const existing = this.issued.get(fp)
    if (existing !== undefined) return existing
    const stored = this.readAll()[fp]
    if (stored) {
      this.issued.set(fp, stored)
      return stored
    }
    const fresh = mint()
    this.issued.set(fp, fresh)
    this.writeAll({ ...this.readAll(), [fp]: fresh })
    return fresh
  }

  release(intent: ConversationCreateIntent, expectedKey: string): boolean {
    const fp = conversationCreateFingerprint(intent)
    if (this.issued.get(fp) !== expectedKey) return false
    const next = { ...this.readAll() }
    const stored = next[fp]
    if (stored !== undefined && stored !== expectedKey) return false
    this.issued.delete(fp)
    delete next[fp]
    this.writeAll(next)
    return true
  }

  has(intent: ConversationCreateIntent): boolean {
    return this.issued.has(conversationCreateFingerprint(intent))
  }

  private readAll(): Record<string, string> {
    try {
      const raw = this.persist?.getItem(this.persistKey)
      if (!raw) return {}
      const parsed: unknown = JSON.parse(raw)
      if (!isPersistedLedger(parsed)) return {}
      return { ...parsed.keys }
    } catch {
      // 无痕模式、坏 JSON、旧版 scope ledger 或裸 UUID：一律当没有。
      // 不绑意图的旧钥匙一旦拿来配新摘要，就是审查里那个首击 409。
      return {}
    }
  }

  private writeAll(keys: Record<string, string>): void {
    try {
      if (Object.keys(keys).length === 0) {
        this.persist?.removeItem(this.persistKey)
        return
      }
      const payload: PersistedLedger = { v: PERSIST_VERSION, keys }
      this.persist?.setItem(this.persistKey, JSON.stringify(payload))
    } catch {
      // 无痕模式读写会抛。降级成这一次只活在内存里。
    }
  }
}

function currentSessionStorage(): CreateConversationKeyStore | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage
  } catch {
    return null
  }
}

/** Chat、Lulu 与项目模块在同一标签页里共用这一份内存 ledger 与存储键。 */
export const conversationCreateKeys = new CreateConversationKeys(currentSessionStorage())

export function releaseCreateConversationKey(
  keys: CreateConversationKeys,
  intent: ConversationCreateIntent,
  expectedKey: string,
  status: number | undefined,
  succeeded: boolean,
): void {
  if (succeeded || isDefinitiveRejection(status)) keys.release(intent, expectedKey)
}
